import React, { useCallback, useDeferredValue, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Table, Button, Space, Modal, message, Input, Select, Tag } from 'antd';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { deleteMessage, getMessages, type AdminMessage } from '../services/api';
import './MessageManage.css';

const { Option } = Select;

const formatTime = (timestamp: number) => new Date(timestamp * 1000).toLocaleString('zh-CN');

type ParsedMessageContent = {
  type: string;
  text: string;
  url: string;
  hash: string;
  duration: number;
};

const mediaTypeByMsgType: Record<number, string> = {
  2: 'image',
  3: 'voice',
  4: 'video',
};

const toStringField = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const toPositiveInt = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 0 ? Math.floor(value) : 0;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value.trim(), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }
  return 0;
};

const parseMessageContent = (content: string, msgType: number): ParsedMessageContent => {
  const trimmed = content.trim();
  const fallbackType = mediaTypeByMsgType[msgType] || 'text';
  const fallback: ParsedMessageContent = {
    type: fallbackType,
    text: '',
    url: '',
    hash: '',
    duration: 0,
  };

  if (!trimmed) {
    return fallback;
  }

  try {
    const decoded: unknown = JSON.parse(trimmed);

    if (typeof decoded === 'string') {
      return {
        ...fallback,
        type: 'text',
        text: decoded.trim(),
      };
    }

    if (decoded && typeof decoded === 'object' && !Array.isArray(decoded)) {
      const decodedMap = decoded as Record<string, unknown>;
      return {
        type: toStringField(decodedMap.type) || fallbackType,
        text: toStringField(decodedMap.text) || toStringField(decodedMap.content) || toStringField(decodedMap.value),
        url: toStringField(decodedMap.url) || toStringField(decodedMap.file_url) || toStringField(decodedMap.media_url),
        hash: toStringField(decodedMap.hash) || toStringField(decodedMap.file_hash),
        duration: toPositiveInt(decodedMap.duration),
      };
    }
  } catch (_) {
    // 忽略 JSON 解析错误，尝试兼容旧格式
  }

  const legacyHash = /hash=([^|\]]+)/.exec(trimmed)?.[1]?.trim() || '';
  const closeBracket = trimmed.indexOf(']');
  const legacyURL = closeBracket !== -1 && closeBracket + 1 < trimmed.length ? trimmed.slice(closeBracket + 1).trim() : '';
  const legacyVoiceDuration = Number.parseInt(/^\[语音\|(\d+)\]/.exec(trimmed)?.[1] || '0', 10) || 0;

  if (trimmed.startsWith('[图片')) {
    return { type: 'image', text: '', url: legacyURL, hash: legacyHash, duration: 0 };
  }
  if (trimmed.startsWith('[视频')) {
    return { type: 'video', text: '', url: legacyURL, hash: legacyHash, duration: 0 };
  }
  if (trimmed.startsWith('[语音')) {
    return { type: 'voice', text: '', url: legacyURL, hash: legacyHash, duration: legacyVoiceDuration };
  }

  // 避免展示原始 JSON 文本（比如异常格式）
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return fallback;
  }

  return {
    type: 'text',
    text: trimmed,
    url: '',
    hash: '',
    duration: 0,
  };
};

const formatMessagePreview = (content: string, msgType: number) => {
  const parsed = parseMessageContent(content, msgType);

  switch (parsed.type) {
    case 'image':
      return parsed.text ? `[图片] ${parsed.text}` : '[图片]';
    case 'video':
      return parsed.text ? `[视频] ${parsed.text}` : '[视频]';
    case 'voice': {
      const seconds = parsed.duration > 0 ? parsed.duration : 1;
      return parsed.text ? `[语音] ${seconds}s ${parsed.text}` : `[语音] ${seconds}s`;
    }
    default:
      if (parsed.text) {
        return parsed.text;
      }
      if (msgType === 2) return '[图片]';
      if (msgType === 3) return '[语音]';
      if (msgType === 4) return '[视频]';
      return '[消息]';
  }
};

const messageTypeOptions = [
  { value: 'all', label: '全部' },
  { value: 'text', label: '文本' },
  { value: 'image', label: '图片' },
  { value: 'voice', label: '语音' },
  { value: 'video', label: '视频' },
];

const messageTypeMap: Record<string, number | undefined> = {
  all: undefined,
  text: 1,
  image: 2,
  voice: 3,
  video: 4,
};

const renderMessageType = (msgType: number) => {
  const map: Record<number, { color: string; label: string }> = {
    1: { color: 'blue', label: '文本' },
    2: { color: 'geekblue', label: '图片' },
    3: { color: 'orange', label: '语音' },
    4: { color: 'purple', label: '视频' },
  };
  return map[msgType] || { color: 'default', label: `类型${msgType}` };
};

const renderMessageBubble = (content: string, record: AdminMessage) => {
  if (record.is_revoke === 2) {
    return (
      <div className="message-bubble-row">
        <div className="message-bubble message-bubble-revoked">该消息已撤回</div>
      </div>
    );
  }

  const parsed = parseMessageContent(content, record.msg_type);
  const preview = formatMessagePreview(content, record.msg_type);
  const hasMedia = (parsed.type === 'image' || parsed.type === 'voice' || parsed.type === 'video') && !!parsed.url;

  return (
    <div className="message-bubble-row">
      <div className={`message-bubble${hasMedia ? ' message-bubble-has-media' : ''}`} title={preview}>
        {parsed.type === 'image' && parsed.url ? (
          <div className="message-media-wrap">
            <a href={parsed.url} target="_blank" rel="noopener noreferrer" className="message-media-link">
              <img src={parsed.url} alt="图片消息" className="message-image" loading="lazy" />
            </a>
            {parsed.text ? <div className="message-caption">{parsed.text}</div> : null}
          </div>
        ) : null}
        {parsed.type === 'voice' && parsed.url ? (
          <div className="message-media-wrap">
            <audio className="message-audio" controls preload="none" src={parsed.url} />
            <div className="message-caption">{parsed.duration > 0 ? `语音 ${parsed.duration}s` : '语音消息'}</div>
            {parsed.text ? <div className="message-caption">{parsed.text}</div> : null}
          </div>
        ) : null}
        {parsed.type === 'video' && parsed.url ? (
          <div className="message-media-wrap">
            <video className="message-video" controls preload="metadata" src={parsed.url} />
            {parsed.text ? <div className="message-caption">{parsed.text}</div> : null}
          </div>
        ) : null}
        {!hasMedia ? preview : null}
        {hasMedia && parsed.url ? (
          <a href={parsed.url} target="_blank" rel="noopener noreferrer" className="message-open-link">
            打开原文件
          </a>
        ) : null}
      </div>
    </div>
  );
};

const MessageManage: React.FC = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<AdminMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const deferredSearchText = useDeferredValue(searchText.trim());
  const [msgType, setMsgType] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [tableScrollY, setTableScrollY] = useState(360);

  const updateTableScrollY = useCallback(() => {
    if (!pageRef.current || !headerRef.current || !tableWrapRef.current) {
      return;
    }

    const containerHeight = pageRef.current.getBoundingClientRect().height;
    const headerHeight = headerRef.current.getBoundingClientRect().height;
    const headerGap = 8;
    const tableWrapTop = tableWrapRef.current.getBoundingClientRect().top;
    const tableWrapHeight = Math.max(0, containerHeight - headerHeight - headerGap);
    const tableWrapBottom = tableWrapTop + tableWrapHeight;

    const tableBodyEl = tableWrapRef.current.querySelector('.ant-table-body') as HTMLElement | null;
    const paginationEl = tableWrapRef.current.querySelector('.ant-table-pagination') as HTMLElement | null;
    if (!tableBodyEl) {
      return;
    }

    const bodyTop = tableBodyEl.getBoundingClientRect().top;
    const paginationRect = paginationEl?.getBoundingClientRect();
    const paginationTop = paginationRect?.top ?? tableWrapBottom - 64;
    const paginationHeight = paginationRect?.height ?? 56;
    const bodyReserve = 12;
    const bottomSafe = 8;
    const maxBodyBottom = Math.min(paginationTop, tableWrapBottom - paginationHeight - bottomSafe);
    const next = Math.max(120, Math.floor(maxBodyBottom - bodyTop - bodyReserve));
    setTableScrollY(prev => (prev === next ? prev : next));
  }, []);

  const loadData = useCallback(async (currentPage = page, currentPageSize = pageSize) => {
    setLoading(true);
    try {
      const res = await getMessages(currentPage, currentPageSize, {
        keyword: deferredSearchText || undefined,
        msgType: messageTypeMap[msgType],
      });
      if (res.code !== 0) {
        message.error(res.message || '加载消息失败');
        return;
      }
      setData(res.data.list);
      setTotal(res.data.total);
    } catch (error) {
      message.error('加载消息失败');
    } finally {
      setLoading(false);
    }
  }, [deferredSearchText, msgType, page, pageSize]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useLayoutEffect(() => {
    updateTableScrollY();
    const id = window.requestAnimationFrame(updateTableScrollY);
    return () => window.cancelAnimationFrame(id);
  }, [updateTableScrollY, pageSize, total, data.length, loading]);

  useEffect(() => {
    updateTableScrollY();
    window.addEventListener('resize', updateTableScrollY);

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        updateTableScrollY();
      });
      if (pageRef.current) {
        observer.observe(pageRef.current);
      }
      if (headerRef.current) {
        observer.observe(headerRef.current);
      }
      if (tableWrapRef.current) {
        observer.observe(tableWrapRef.current);
      }
    }

    return () => {
      window.removeEventListener('resize', updateTableScrollY);
      observer?.disconnect();
    };
  }, [updateTableScrollY]);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '发送者', dataIndex: 'from_user', key: 'from_user' },
    { title: '接收者', dataIndex: 'to_user', key: 'to_user' },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      width: 380,
      render: (content: string, record: AdminMessage) => renderMessageBubble(content, record),
    },
    { title: '类型', dataIndex: 'msg_type', key: 'msg_type', render: (messageType: number) => {
      const item = renderMessageType(messageType);
      return <Tag color={item.color}>{item.label}</Tag>;
    }},
    { title: '状态', dataIndex: 'status', key: 'status', render: (status: number, record: AdminMessage) => (
      <Tag color={record.is_revoke === 2 ? 'default' : status === 1 ? 'red' : 'green'}>
        {record.is_revoke === 2 ? '已撤回' : status === 1 ? '未读' : '已读'}
      </Tag>
    )},
    { title: '时间', dataIndex: 'created_at', key: 'created_at', render: (createdAt: number) => formatTime(createdAt) },
    { title: '操作', key: 'action', width: 100, render: (_: unknown, record: AdminMessage) => (
      <Button type="link" danger icon={<DeleteOutlined />} size="small" onClick={() => handleDelete(record.id)}>删除</Button>
    )},
  ];

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除该消息吗？',
      onOk: async () => {
        const res = await deleteMessage(id);
        if (res.code !== 0) {
          message.error(res.message || '删除失败');
          return;
        }

        message.success('删除成功');
        const nextPage = data.length === 1 && page > 1 ? page - 1 : page;
        if (nextPage !== page) {
          setPage(nextPage);
          return;
        }
        void loadData(nextPage, pageSize);
      },
    });
  };

  return (
    <div className="message-manage-page" ref={pageRef}>
      <div className="message-manage-header" ref={headerRef}>
        <h1 className="message-manage-title">消息管理</h1>
        <Space className="message-manage-toolbar">
          <Input
            placeholder="搜索用户或内容"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => {
              setPage(1);
              setSearchText(e.target.value);
            }}
            style={{ width: 240 }}
            allowClear
          />
          <Select value={msgType} onChange={value => {
            setPage(1);
            setMsgType(value);
          }} style={{ width: 120 }}>
            {messageTypeOptions.map(option => (
              <Option key={option.value} value={option.value}>{option.label}</Option>
            ))}
          </Select>
        </Space>
      </div>
      <div className="message-manage-table-wrap" ref={tableWrapRef}>
        <Table
          className="message-manage-table"
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ y: tableScrollY }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
        />
      </div>
    </div>
  );
};

export default MessageManage;
